import React, { useState } from 'react';
import { Badge } from '../../../shared/components/ui';

/**
 * Confidence badge component
 */
function ConfidenceBadge({ confidence }) {
  if (confidence === undefined || confidence === null) return null;

  const percent = Math.round(confidence * 100);
  let variant = 'green';
  if (percent < 60) variant = 'red';
  else if (percent < 80) variant = 'yellow';

  return (
    <Badge variant={variant} className="text-xs">
      {percent}% confidence
    </Badge>
  );
}

/**
 * Division section within a document
 */
function DivisionSection({ division }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
      {/* Division Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="flex items-center gap-3">
          <svg
            className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="font-medium text-gray-900 dark:text-gray-100">
            Division {String(division.divisionNumber).padStart(2, '0')}: {division.divisionTitle}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {division.sheetCount > 1 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {division.sheetCount} pages analyzed
            </span>
          )}
          <ConfidenceBadge confidence={division.confidence} />
        </div>
      </button>

      {/* AI Analysis content — shown directly when the division is expanded. */}
      {isExpanded && (
        <div className="p-4 bg-blue-50/50 dark:bg-blue-900/10 border-t border-blue-100 dark:border-blue-900/40">
          {division.extractionNotes ? (
            <div className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
              {division.extractionNotes}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
              No AI analysis available for this division.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * DocumentFactsEditor - Renders the AI-analysis-per-division drawer for a document.
 *
 * The parent DocumentRow already shows the document name, file size, page count,
 * and handles the open/close state, so this component renders only the contents.
 */
export function DocumentFactsEditor({ divisions }) {
  if (!divisions || divisions.length === 0) {
    return (
      <div className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
        <p className="text-sm">No AI analysis available for this document yet</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          Analysis will be available once document processing is complete
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {divisions.map((division) => (
        <DivisionSection key={division.divisionNumber} division={division} />
      ))}
    </div>
  );
}

export default DocumentFactsEditor;
